import {
  Building2,
  CalendarClock,
  CalendarRange,
  ChevronDown,
  Play,
  Settings,
  Trophy,
  UserCog,
  Users,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet } from 'react-router-dom';
import { useLicenseNotice } from '@/app/providers/license-notice-context';
import { LogoutButton, useAuth } from '@/features/auth';
import { clubRoleLabelKey, useSelectedClub } from '@/features/club';
import { fullNameOf, useCurrentUserQuery } from '@/features/user';
import { cn } from '@/shared/lib/cn';
import {
  AppShell,
  Avatar,
  DropdownMenu,
  Logo,
  NotificationBell,
  ThemeToggle,
  type NavItem,
} from '@/shared/ui';

/** Wyglad pozycji stopki paska bocznego — ten sam co pozycji nawigacji. */
const footerItemClass =
  'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium ' +
  'text-fg-muted transition-colors duration-150 hover:bg-surface-hover hover:text-fg ' +
  '[&_svg]:size-5';

/**
 * Powloka widokow po zalogowaniu — pasek boczny i pasek gorny z makiety `start.png`.
 *
 * Nawigacja jest JEDNA i taka sama na kazdym widoku: menu turniejow, bo to
 * jedyny dzialajacy obszar aplikacji. Zakladki zmieniaja sie wylacznie na
 * podstawie uprawnien zalogowanego — strony administracyjne (Kluby,
 * Uzytkownicy) widzi tylko ADMIN, w stopce nad Ustawieniami.
 *
 * Kontekst pracy (klub i uzytkownik z jego rola w tym klubie) siedzi w pasku gornym,
 * a nie w kilku miejscach naraz — inaczej ta sama nazwa klubu powtarzalaby sie
 * na ekranie dwa razy. Na dole paska bocznego zostaje samo logo klubu.
 */
export function AppLayout() {
  const { t } = useTranslation('common');
  const { t: tStart } = useTranslation('start');
  const { showLicenseNotice } = useLicenseNotice();
  const { user } = useAuth();
  const { data: currentUser } = useCurrentUserQuery();
  const { clubs, club, selectClub } = useSelectedClub();

  const isAdmin = user?.roles.includes('ADMIN') ?? false;

  /** Jedno wspolne menu — pozycje obszaru turniejow. */
  const navItems: readonly NavItem[] = [
    // `end`, bo bez niego pozycja swiecilaby sie na kazdym podwidoku obszaru.
    { to: '/turnieje', label: t('nav.myTournaments'), icon: <Trophy />, end: true },
    { to: '/turnieje/zawodnicy', label: t('nav.clubPlayers'), icon: <Users /> },
    {
      // Jeden widok: korty klubu + kalendarz rozmieszczania meczów.
      to: '/turnieje/infrastruktura',
      label: t('nav.infrastructure'),
      icon: <CalendarRange />,
    },
    {
      // Trwające mecze ze wszystkich turniejów klubu — jeden rzut oka na to,
      // co dzieje się teraz na kortach.
      to: '/turnieje/rozpoczete',
      label: t('nav.liveMatches'),
      icon: <Play />,
    },
    {
      // Mecze zaplanowane, ktore sie jeszcze nie rozpoczely — termin i kort.
      to: '/turnieje/zaplanowane',
      label: t('nav.scheduledMatches'),
      icon: <CalendarClock />,
    },
  ];

  const userName = currentUser ? fullNameOf(currentUser) : '';

  // Rola w klubie opisuje, w jakiej roli uzytkownik pracuje w tym konkretnym
  // klubie, wiec ma pierwszenstwo nad globalna. Przychodzi razem z klubem
  // z `GET /api/clubs/my`. Role globalna pokazujemy tylko wtedy, gdy uzytkownik
  // nie nalezy do zadnego klubu — inaczej nie mielibysmy czego wyswietlic.
  const clubRoleKey = club ? clubRoleLabelKey(club.roleName) : null;
  const globalRole = user?.roles.includes('ADMIN')
    ? 'ADMIN'
    : user?.roles.includes('OWNER')
      ? 'OWNER'
      : (user?.roles[0] ?? null);

  const roleLabel = club
    ? (clubRoleKey ? t(clubRoleKey) : club.roleName)
    : globalRole
      ? t(`roles.${globalRole}`)
      : null;

  /** Stopka paska — ustawienia i wylogowanie pod logo klubu. */
  const sidebarFooter = (
    <div className="flex flex-col gap-1">
      {club && (
        <div className="mb-2 flex justify-center px-2 py-1">
          <Avatar src={club.logoUrl} name={club.name} size="xl" fit="contain" />
        </div>
      )}

      {/* Strony administracyjne — tylko dla ADMIN-a, tuz nad Ustawieniami. */}
      {isAdmin && (
        <>
          <Link to="/kluby" className={footerItemClass}>
            <Building2 />
            {t('nav.clubs')}
          </Link>
          <Link to="/uzytkownicy" className={footerItemClass}>
            <UserCog />
            {t('nav.usersAdmin')}
          </Link>
        </>
      )}

      <Link to="/ustawienia" className={footerItemClass}>
        <Settings />
        {t('nav.settings')}
      </Link>

      <LogoutButton variant="ghost" fullWidth className="justify-start px-3" />
    </div>
  );

  return (
    <AppShell
      items={navItems}
      sidebarHeader={
        // Bez strzalki powrotu — turnieje sa strona glowna aplikacji
        // (trasa `/` i tak przekierowuje na `/turnieje`).
        <Logo size="md" />
      }
      sidebarFooter={sidebarFooter}
      mobileHeader={<Logo size="sm" />}
      // Na waskim ekranie pasek gorny tresci jest ciasny, wiec przelacznik
      // motywu przenosi sie na pasek naglowka, na lewo od przycisku menu.
      mobileActions={<ThemeToggle />}
    >
      <div className="mx-auto flex w-full max-w-[1500px] flex-col gap-5 px-5 py-5 lg:px-8 lg:py-6">
        {/* Pasek gorny: motyw, powiadomienia oraz kontekst pracy — klub i uzytkownik. */}
        <div className="flex items-center justify-end gap-2">
          {/* Ponizej `lg` przelacznik stoi na pasku naglowka (patrz `mobileActions`),
              wiec tutaj pokazujemy go dopiero od tego breakpointu — inaczej byliby dwaj. */}
          <ThemeToggle className="hidden lg:inline-flex" />
          {/* Na waskim ekranie pasek zjada przelacznik klubu i nazwe uzytkownika,
              a dzwonek nie prowadzi dzis do zadnego widoku — chowamy go do `sm`. */}
          <NotificationBell
            onClick={() => showLicenseNotice(t('nav.notifications'))}
            className="hidden sm:inline-flex"
          />

          {clubs.length > 0 && (
            <DropdownMenu
              trigger={(triggerProps) => (
                <button
                  type="button"
                  {...triggerProps}
                  className={cn(
                    'border-border-strong bg-surface flex items-center gap-2.5 rounded-full',
                    'py-1.5 pr-3 pl-1.5 shadow-xs transition-colors',
                    'hover:bg-surface-hover',
                  )}
                >
                  <Avatar
                    src={club?.logoUrl ?? null}
                    name={club?.name ?? ''}
                    size="sm"
                    fit="contain"
                  />
                  <span className="text-fg-strong max-w-[12rem] truncate text-sm font-semibold">
                    {club?.name ?? tStart('clubPicker.label')}
                  </span>
                  <ChevronDown className="text-fg-subtle size-4 shrink-0" />
                </button>
              )}
              items={clubs.map((item) => ({
                label: item.name,
                onSelect: () => selectClub(item.id),
              }))}
            />
          )}

          {currentUser && (
            <Link
              to="/konto"
              aria-label={t('nav.account')}
              className={cn(
                'border-border-strong bg-surface flex items-center gap-2.5 rounded-full',
                'py-1.5 pr-3 pl-1.5 shadow-xs transition-colors',
                'hover:bg-surface-hover',
              )}
            >
              <Avatar name={userName} size="sm" />
              <span className="flex min-w-0 flex-col text-left">
                <span className="text-fg-strong max-w-[10rem] truncate text-sm leading-tight font-semibold">
                  {userName}
                </span>
                {roleLabel && (
                  <span className="text-fg-subtle max-w-[10rem] truncate text-[0.6875rem] leading-tight">
                    {roleLabel}
                  </span>
                )}
              </span>
            </Link>
          )}
        </div>

        <Outlet />
      </div>
    </AppShell>
  );
}
